import { journalBoundaryContext, boundaryValue } from '../ability/boundary-journal.js';
import { activeStatuses, ec, HandBack, record, upper, type JanusCtx, type Row } from '../ability/context.js';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect.js';
import { connectionSurvivors } from '../ability/statuses/status-effect-removal-helpers.js';
import { boundaryAttempt, boundaryVerdict, creatureName, resolveGmZoneEntry, resolveGmZoneExit, type GmBoundaryVerdict } from '../statuses/gm-boundary.js';
import { resolveLandingSupport } from '../statuses/removal-support.js';
import { encounterField, posCoord } from '../../shared/array-types.js';
import { bodyInSolid } from '../../shared/burrow-route.js';
import { displacementDice, solidContextFromEncounter, teleportDisplacement } from '../../shared/solid-material.js';
import { encounterIsShapeless, shapelessTeleportLanding, resolveTeleportTraversalGates, settleMovementZones, squeezeStateAt, type PlainWalkReaders } from './plain-walk-scope.js';
import { uniqueZoneEntrants } from '../zones/walk-zone-crossings.js';
import { LINK_STATUS_IDS, planCompoundWalk, type Point } from './compound-drag.js';
import { squeezeDelta, statusItemIdOf, SQUEEZED_ITEM_ID, VERY_SQUEEZED_ITEM_ID } from './squeeze-at-rest.js';
import { BURROWED_ITEM_ID } from './burrow-move.js';
import { teleportBodies, teleportDeniedByRestrainer, TELEPORT_BINDING_MESSAGE } from './teleport-move.js';
import { encounterWaterModel } from './traversal-3d.js';
import { waterContactDepth } from './water-wet.js';
import { moverHasMovementFlag } from './difficult-terrain.js';
import { skipUnported } from '../unported.js';
import { escapeHtml } from '../../shared/cosmetic-card.js';
import type { WalkZoneResolution } from '../zones/walk-zone-crossings.js';
import type { PaoeEntryRecords } from '../ability/paoe-entry-once.js';
import { frameCopyOnRead, retainHeldBase } from '../ability/frame-snapshot.js';
import { resolveTeleportDisplacementDamage } from './teleport-displacement-damage.js';
export { TELEPORT_DISPLACEMENT_ID } from './teleport-displacement-damage.js';

export type TeleportZoneReader = (before: Row, after: Row, actorId: string) => WalkZoneResolution;
const HUNKER = 'BE6DC590-4EB7-B297-5AC6-8E47AA4E00EF';
const CLEARS = new Map([[LINK_STATUS_IDS.grappled, 'Grappled'], ['E34AF232-5438-88FC-9A9F-73850CCCFD51', 'Restrained'],
  ['F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', 'Prone']]);

function place(member: Row, point: Point): void {
  const position = { ...record(member.position), x_pos: point.x, y_pos: point.y, z_pos: point.z };
  member.position = position;
  Object.assign(ec(member), position);
}
function scene(ctx: JanusCtx): Row { return { ...ctx.encounter, encounter_creatures_arr: [...ctx.members.values()] }; }

/** One browser verdict, saved atomically with offer consumption. No route or movement spend. */
export function resolveTeleportOfferLanding(ctx: JanusCtx, actorId: string, requested: Point, rangeFt: number,
  page: PlainWalkReaders, zones: TeleportZoneReader, entries?: PaoeEntryRecords): GmBoundaryVerdict {
  actorId = upper(actorId);
  let actor = ctx.members.get(actorId);
  if (!actor) throw new HandBack('teleport-offer:actor-not-held');
  const read: PlainWalkReaders = { ...page, statuses: member => activeStatuses(ctx, member),
    position: member => [posCoord(member, 'x_pos') ?? 0, posCoord(member, 'y_pos') ?? 0, posCoord(member, 'z_pos') ?? 0],
    hasStatus: (member, key) => page.hasStatus({ ...member, statuses: { target: activeStatuses(ctx, member) } }, key),
    zoneWalk: () => ({ kind: 'clear' }) };
  const [x, y, z] = read.position(actor), origin = { x, y, z };
  const distance = Math.hypot(requested.x - x, requested.y - y, requested.z - z);
  if (![requested.x, requested.y, requested.z, rangeFt].every(Number.isFinite) || distance < 0.01 || !(rangeFt > 0) || distance > rangeFt + 0.001)
    throw new HandBack('teleport-offer:destination-out-of-range');
  if (teleportDeniedByRestrainer(scene(ctx), actor, read)) throw new HandBack(TELEPORT_BINDING_MESSAGE);
  const geometry = read.geometry(actor), size = geometry?.rulesDiameter ?? geometry?.diameter;
  if (!size || size <= 0) throw new HandBack('teleport-offer:geometry-not-held');
  const world = scene(ctx);
  let displacement;
  if (encounterIsShapeless(world)) {
    const placed = shapelessTeleportLanding(actorId, requested, origin, world, actor, read);
    if ('why' in placed) throw new HandBack(`teleport-offer:shapeless:${placed.why}`);
    if ('blocked' in placed) throw new HandBack('teleport-offer:no-clear-space');
    displacement = { ...placed, blocked: false, dice: displacementDice(placed.displacedFt) };
  } else {
    const bodies = teleportBodies(world, actorId, read);
    if (!bodies) throw new HandBack('teleport-offer:other-geometry-not-held');
    displacement = teleportDisplacement(solidContextFromEncounter(world, read.statuses), bodies, origin, requested, size / 2, size);
  }
  if (displacement.blocked) throw new HandBack('teleport-offer:no-clear-space');
  const landing = Object.fromEntries(Object.entries(displacement.point).map(([key, value]) => [key, Number(value.toFixed(3))])) as Point;
  const gate = resolveTeleportTraversalGates(actorId, origin, landing, scene(ctx), actor, read, undefined, { offerLanding: true, settlement: true });
  if (!gate.supported) throw new HandBack(gate.refused ? gate.message : `teleport-offer:${gate.reason}`);
  // A fresh landing frame already pins its member inputs in originals. Keep those
  // before facts; only encounter-level trial writes need a separate lazy branch.
  const before = retainHeldBase(ctx.encounter, { ...world, encounter_creatures_arr: [...ctx.originals.values()] });
  ctx.encounter = frameCopyOnRead(ctx.encounter, new Map(), { catalogs: 'share' }).encounter;
  const skip = (id: string, duty: string): void => { ctx.rows.push(skipUnported(`teleport-offer:${escapeHtml(creatureName(ctx, id))}:${duty}`)); };
  ctx = journalBoundaryContext(ctx); actor = boundaryValue(ctx, actor);
  boundaryAttempt(ctx, 'teleport-offer:landing', () => {
    // Grappler-initiated travel carries its grapplee. A grapplee initiating travel releases its own link.
    const cleared = new Set<string>();
    for (const status of [...read.statuses(actor)]) {
      const itemId = statusItemIdOf(status);
      if (!CLEARS.has(itemId)) continue;
      cleared.add(itemId);
      const peers = connectionSurvivors(ctx, upper(status.status_connected_id), upper(status.status_id));
      resolveGmZoneExit(ctx, { statusId: upper(status.status_id), itemId, targetId: actorId }, { support: false, linkTeardown: true });
      for (const peer of peers) {
        if (itemId === LINK_STATUS_IDS.grappled && statusItemIdOf(peer.status) === LINK_STATUS_IDS.grappling && upper(peer.status.status_caster_id) === actorId) {
          if (peer.member) resolveGmZoneExit(ctx, { statusId: upper(peer.status.status_id), itemId: statusItemIdOf(peer.status), targetId: peer.holder }, { support: false, linkTeardown: true });
          else skip(actorId, 'linked-peer-not-held');
        }
      }
    }
    if (cleared.size) ctx.rows.push({ roll_type_1: 'teleport_cleared_conditions', roll_type_2: [...CLEARS.keys()].filter(id => cleared.has(id)).join(','),
      roll_item_id: 'DF516479-F164-87FA-7BB8-6F268BF1DCE7', roll_by: actorId, roll_to: null, roll_result: cleared.size, roll_private: 0,
      rolls_full: `Teleport cleared: ${[...CLEARS].filter(([id]) => cleared.has(id)).map(([, label]) => label).join(', ')} (teleport movement).` });
    place(ctx.members.get(actorId)!, landing);
    resolveLandingSupport(ctx, actorId);
    const mover = ctx.members.get(actorId)!;
    const at = read.position(mover), final = { x: at[0], y: at[1], z: at[2] };
    const rawShape = encounterField(ctx.encounter, 'encounter_shape');
    const shape = record(typeof rawShape === 'string' ? JSON.parse(rawShape) : rawShape);
    const compound = planCompoundWalk({ encounter: scene(ctx), mover, origin, destination: final, shape: shape as never, teleport: true },
      { ...read, baseSizeFt: row => { const body = read.geometry(row); return body && body.rulesDiameter === undefined ? body.diameter : null; },
        heightFt: row => read.geometry(row)?.height ?? null });
    if (compound.kind === 'handback') throw new HandBack(`teleport-offer:${compound.reason}`);
    const moved = [actorId];
    if (compound.kind === 'move') for (const member of compound.members) { place(ctx.members.get(member.id)!, member.after); moved.push(member.id); }
    for (const id of moved) {
      const member = ctx.members.get(id)!;
      const [mx, my, mz] = read.position(member);
      const remove = (status: Row): void => resolveGmZoneExit(ctx, { statusId: upper(status.status_id), itemId: statusItemIdOf(status), targetId: id }, { support: false });
      for (const status of [...read.statuses(member)]) if (statusItemIdOf(status) === HUNKER) remove(status);
      const state = squeezeStateAt(scene(ctx), member, read, mx, my, mz);
      if (!state) throw new HandBack('teleport-offer:squeeze-geometry');
      const statuses = read.statuses(member);
      const delta = squeezeDelta(state, { squeezed: statuses.filter(s => statusItemIdOf(s) === SQUEEZED_ITEM_ID).map(s => upper(s.status_id)),
        very: statuses.filter(s => statusItemIdOf(s) === VERY_SQUEEZED_ITEM_ID).map(s => upper(s.status_id)) });
      for (const status of statuses) if (delta.remove.includes(upper(status.status_id))) remove(status);
      for (const itemId of delta.add) {
        const definition = ctx.read.definition(itemId);
        if (!definition) throw new HandBack('teleport-offer:squeeze-definition-not-held');
        applyStatusEffect(ctx, { abilityId: itemId, casterId: id, targetIds: [id], rawDiceMod: 0, rawDiceAdvDisStacks: 0,
          useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: id }, definition, itemId, 'status_effect_applied', member, member,
          { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true);
      }
      const body = read.geometry(member), width = body?.rulesDiameter ?? body?.diameter;
      if (!width) throw new HandBack('teleport-offer:follower-geometry');
      if (!bodyInSolid(solidContextFromEncounter(scene(ctx), read.statuses), { x: mx, y: my, z: mz, seg: 0 }, width / 2, width))
        for (const status of [...read.statuses(member)]) if (statusItemIdOf(status) === BURROWED_ITEM_ID) remove(status);
      if (!moverHasMovementFlag(member, read, 'walks_on_water_and_ice') && waterContactDepth(encounterWaterModel(scene(ctx)), mx, my, mz, width) > 0)
        skip(id, 'water-wet-unported');
    }
    // The existing walk classifier attaches exact entry/exit duties, using endpoint membership supplied by the adapter.
    const entered = new Set<string>();
    for (const id of moved) {
      const member = ctx.members.get(id)!, [mx, my, mz] = read.position(member);
      const scoped = settleMovementZones({ schema: 'janus.movement.m1a.v1', actor_id: id, origin,
        destination: { x: mx, y: my, z: mz }, route: [[mx, my, mz], [mx, my, mz]], updates: { x_pos: mx, y_pos: my, z_pos: mz }, speed_cost: 0 },
        { ...read, zoneWalk: () => zones(before, scene(ctx), id) });
      if (!scoped.supported) throw new HandBack(`teleport-offer:${scoped.refused ? scoped.message : scoped.detail?.zones ?? scoped.reason}`);
      const entrants = uniqueZoneEntrants([{ actorId: id, entries: scoped.walk.zone_entries ?? [] }, ...(scoped.walk.follower_zone_entries ?? [])], entered);
      if (entrants.length && !entries) throw new HandBack('teleport-offer:entry-turn-records-not-held');
      for (const entrant of entrants) for (const entry of entrant.entries)
        resolveGmZoneEntry(ctx, { abilityId: entry.originId, casterId: entry.casterId, enteringId: entrant.actorId, statusConnectedId: entry.connectedId, forcedExtreme: entry.forcedExtreme }, entries);
      for (const strip of scoped.walk.zone_exits ?? []) {
        const owner = ctx.members.get(upper(strip.targetId)) ?? ctx.encounter;
        const status = read.statuses(owner).find(s => upper(s.status_id) === upper(strip.statusId));
        if (status) resolveGmZoneExit(ctx, { ...strip, itemId: statusItemIdOf(status) });
      }
    }
    if (displacement.dice > 0) boundaryAttempt(ctx, `teleport-offer:${escapeHtml(creatureName(ctx, actorId))}:teleport-displacement-damage:${displacement.displacedFt}ft:${displacement.dice}x2d10`, () =>
      resolveTeleportDisplacementDamage(ctx, actorId, displacement.dice));
  }, true);
  const verdict = boundaryVerdict(ctx);
  // Coordinates are read through the page position reader; even a lean-elided zero is part of the saved landing.
  for (const [id, member] of ctx.members) if (id === actorId || Object.keys(verdict.ec[id] ?? {}).some(key => /_pos$/.test(key))) {
    const [mx, my, mz] = read.position(member);
    Object.assign(verdict.ec[id] ??= {}, { x_pos: mx, y_pos: my, z_pos: mz });
  }
  for (const status of verdict.statusesAdded) (status as unknown as Row).status_id ??= ctx.read.uuid();
  return verdict;
}
